import axios from '|/helpers/axios';
import { ReviewSummary } from './review-summary-types';

export const APP_ENDPOINTS = {
  GET_DASHBOARD: `${process.env.DRIVER_API_URL}/api/v1/dashboard`,
  GET_SUMMARY: `${process.env.PURCHASE_API_URL}/api/v1/summary`,
  GET_AVAILABLE_OPTIONS: `${process.env.API_URL}/api/v1/order/availableoptions`,
  GET_REVIEW_SUMMARY: `${process.env.PURCHASE_API_URL}/api/v1/review`,
  CANCEL_ORDER: `${process.env.PURCHASE_API_URL}/api/v1/cancel`,
  ROLLBACK: `${process.env.PURCHASE_API_URL}/api/v1/roll-back`,
  PAUSE_LOCK_TIMER: `${process.env.API_URL}/api/v1/locktimer/pause`,
  GET_ALL_POSSIBLE_STEPS: `${process.env.DRIVER_API_URL}/api/v1/dashboard/all-possible-steps`,
  GET_TRADE_REQUIREMENT: `${process.env.CUSTOMER_VERIFICATIONS_API_URL}/api/v1/proofOfTrade/bring-title-requirement`
};

export enum StepName {
  'PersonalDetails' = 'PersonalDetails',
  'TradeIn' = 'TradeIn',
  'CoF' = 'CoF',
  'Gap' = 'Gap',
  'PickupDelivery' = 'PickupDelivery',
  'VSC' = 'VSC',
  'DL' = 'DL',
  'IdentityVerification' = 'IdentityVerification',
  'Payment' = 'Payment',
  'Autopay' = 'Autopay',
  'Review' = 'Review',
  'Identity' = 'Identity',
  'BAV' = 'BAV',
  'Income' = 'Income',
  'Address' = 'Address',
  'TradeVerification' = 'TradeVerification',
  'AdditionalIncome' = 'AdditionalIncome',
  'Schedule' = 'Schedule',
  'Insurance' = 'Insurance',
  'Dashboard' = 'Dashboard',
  'CaseSubmission' = 'CaseSubmission',
  'CleanVehicleTaxCredit' = 'CleanVehicleTaxCredit',
  'Incentives' = 'Incentives',
  'PlaceOrder' = 'PlaceOrder',
  'BuyerIdentity' = 'BuyerIdentity',
  'CoSignerIdentity' = 'CoSignerIdentity',
  'Accessories' = 'Accessories'
}

export type Section = 1 | 2 | 3 | 4;

export interface Step {
  isComplete: boolean;
  isEnabled: boolean;
  name: string;
  order: number;
  renderer: string;
  section: Section;
  sectionName: string;
  status: string;
  url: string;
  id: StepName;
  description?: string;
}

export interface DashboardSection {
  sectionId: number;
  sectionName: string;
}

export interface Dashboard {
  steps: Step[];
  sections?: DashboardSection[];
}

export interface Address {
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
}

interface Breakdown {
  /** @format double */
  shippingFee?: number;
  /** @format double */
  cash?: number;
  /** @format double */
  loanAmount?: number;
  /** @format double */
  dueAtOrder?: number;
}

interface Customer {
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  suffix?: string | null;
  address?: Address;
  phoneNumber?: string | null;
  emailAddress?: string | null;
  isEvTaxCreditEligible?: boolean | null;
}

interface Details {
  /** @format double */
  cashDown?: number;
  /** @format double */
  shippingFee?: number;
  /** @format double */
  tradeDown?: number;
}

interface Discount {
  description?: string | null;
  /** @format double */
  amount?: number;
  type?: string;
}

interface Financing {
  hasCoApplicant?: boolean;
  isEligibleForCoApplicant?: boolean;
  isCoApplicantToggleOn?: boolean;
}

export interface Fee {
  description?: string | null;
  /** @format double */
  amount?: number;
}

interface Funding {
  type?: string | null;
  institution?: string | null;
  thirdParty?: ThirdParty;
  loan?: Loan;
}

interface Gap {
  status?: string | null;
  /** @format double */
  price?: number;
}

interface Loan {
  /** @format int32 */
  term?: number;
  /** @format double */
  down?: number;
  /** @format double */
  payment?: number;
  /** @format double */
  apr?: number;
  /** @format double */
  amountFinanced?: number;
  details?: Details;
}

type PaymentSelectionType =
  | 'BankAccount'
  | 'Card'
  | 'DigitalWallet'
  | 'CashiersCheck'
  | 'WireTransfer';

interface PaymentAccount {
  type?: string | null;
  accountId?: string | null;
  mask?: string | null;
  cardBrand?: string | null;
  digitalWallet?: string | null;
  selectionType?: PaymentSelectionType | null;
}

interface Payment {
  creditFromPreviousOrder?: number;
  dueAtOrder?: number;
  shippingFee?: number;
  cashDown?: number;
  refundDue?: number;
  isShippingFeeNonRefundable?: boolean;
  paymentAccounts?: PaymentAccount[];
}

interface Product {
  type?: string | null;
  /** @format double */
  price?: number;
  /** @format int32 */
  months?: number;
  /** @format int32 */
  mileage?: number;
  status?: string | null;
}

interface Rebate {
  name?: string | null;
  /** @format double */
  amount?: number;
}

export interface Marketing {
  hashedEmailAddress?: string | null;
  hashedFirstName?: string | null;
  hashedLastName?: string | null;
  hashedDateOfBirth?: string | null;
  hashedBirthMonthAndDay?: string | null;
  hashedBirthYear?: string | null;
  hashedCity?: string | null;
  hashedState?: string | null;
  hashedZip5?: string | null;
  hashedPhoneNumber?: string | null;
}

export interface Swap {
  hasActiveSwap: boolean;
  vehicle?: Vehicle;
}

export interface Incentive {
  amount: number;
  description: string;
  programCode: string;
  expirationDate: string;
}

export interface Experiment {
  key: string;
  bucket: string;
}

export interface Summary {
  customer?: Customer;
  cobuyer?: Customer;
  vehicle?: Vehicle;
  tradeIn?: TradeIn;
  product?: Product;
  gap?: Gap;
  payment?: Payment;
  financing?: Financing;
  funding?: Funding;
  enrolledInAutopay?: boolean | null;
  transfer?: Transfer;
  fees?: Fee[] | null;
  discounts?: Discount[] | null;
  incentives?: Incentive[] | null;
  rebates?: Rebate[] | null;
  totals?: Totals;
  orderPlacedDateTime?: string | null;
  cartCreatedDateTimeUtc?: string | null;
  marketing?: Marketing;
  swap?: Swap;
  carvanaDiscount?: number;
  experiments?: Experiment[];
}

interface ThirdParty {
  /** @format double */
  loanAmount?: number;
  lenderName?: string | null;
  apr?: string | null;
  termLength?: string | null;
  cosignerName?: string | null;
  breakdown?: Breakdown;
}

interface Totals {
  /** @format double */
  totalPrice?: number;
  /** @format double */
  vehicleStickerPrice?: number;
  /** @format double */
  taxes?: number;
  /** @format double */
  fees?: number;
  /** @format double */
  discounts?: number;
  /** @format double */
  rebates?: number;
  /** @format double */
  deliveryCharge?: number;
  /** @format double */
  shippingFee?: number;
  /** @format double */
  tradeInValue?: number;
  /** @format double */
  products?: number;
  /** @format double */
  taxesWithoutTrade?: number;
  /** @format double */
  tradeInTaxSavings?: number;
  savingsAndIncentives?: number;
  incentives?: number;
}

interface TradeIn {
  /** @format double */
  lienAmount?: number;
  /** @format double */
  value?: number;
  /** @format int32 */
  year?: number;
  make?: string | null;
  model?: string | null;
  status?: string | null;
  acquisitionId?: number;
  acquisitionName?: string | null;
  trim?: string | null;
  odometer?: number;
  isTradeInEligible?: boolean;
  vin?: string | null;
  imageUrl?: string | null;
  /** @format date-time */
  expiresOn?: string | null;
  /** @format double */
  tradeIncentive?: number;
  lienHolderName?: string | null;
}

interface Transfer {
  deliveryType?: string | null;
  address?: Address;
  serviceTimeZone?: string | null;
  scheduledTimeSlot?: string | null;
  windowStartTime?: string | null;
  windowEndTime?: string | null;
  locationType?: string | null;
}

export interface Vehicle {
  /** @format int64 */
  id?: number;
  /** @format int32 */
  year?: number;
  make?: string | null;
  model?: string | null;
  vin?: string | null;
  isAvailable?: boolean | null;
  /** @format date-time */
  unlockDateTime?: string;
  engineDescription?: string;
  odometer?: number;
  trim?: string;
  primaryImageUrl?: string | null;
  fallbackImageUrl?: string | null;
  stockNumber?: number | null;
  inventoryType?: string | null;
  isEvTaxCreditEligible?: boolean | null;
  fuelDescription?: string | null;
  msrp?: number;
  vehicleType?: string | null;
}

export interface SimpleStep {
  stepId: StepName;
  name: string;
  url: string;
  order: number;
  section: Section;
  sectionName: string;
}

export const getDashboard = (abortSignal?: AbortSignal) =>
  axios
    .get<Dashboard>(APP_ENDPOINTS.GET_DASHBOARD, {
      signal: abortSignal
    })
    .then(res => (res.status === 204 ? null : res.data));

export const getAllPossibleSteps = () =>
  axios.get<SimpleStep[]>(APP_ENDPOINTS.GET_ALL_POSSIBLE_STEPS).then(res => res.data);

export const getSummary = (abortSignal?: AbortSignal) =>
  axios
    .get<Summary>(APP_ENDPOINTS.GET_SUMMARY, {
      signal: abortSignal
    })
    .then(res => (res.status === 204 ? null : res.data));

export const getReviewSummary = (abortSignal?: AbortSignal) =>
  axios
    .get<ReviewSummary>(APP_ENDPOINTS.GET_REVIEW_SUMMARY, {
      signal: abortSignal
    })
    .then(res => (res.status === 204 ? null : res.data));

export const getTradeRequirement = (abortSignal?: AbortSignal) =>
  axios
    .get<boolean>(APP_ENDPOINTS.GET_TRADE_REQUIREMENT, {
      signal: abortSignal
    })
    .then(res => res.data);

interface CancelRequest {
  reasons: string[];
  notes?: string;
  executeReturnVehicle?: boolean;
}

export const cancelOrder = (request: CancelRequest) =>
  axios.post(APP_ENDPOINTS.CANCEL_ORDER, request).then(() => void 0);

interface RollbackRequest {
  cancelTransfer: boolean;
  keepReviewOrder?: boolean;
  reasons?: string[];
  notes?: string;
  lockTimeMinutesToExtend?: number;
}

interface CoBuyer {
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  isSigned: boolean;
  signedDateTime: string;
  emailSent: boolean;
  emailSentDateTime: string;
}

interface Primary {
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  isSigned: boolean;
  signedDateTime: string;
  emailSent: boolean;
  emailSentDateTime: string;
}

export interface AvailableOptions {
  canRollback: boolean;
  canDownloadCoPilot: boolean;
  canSignContracts: boolean;
  canReschedule: boolean;
  isPlaidCustomer: boolean;
  needsToScheduleTimeSlot: boolean;
  inventoryType: string;
  isDirectDelivery: boolean;
  isVehicleLockPaused: boolean;
  purchaseId: number;
  contractsStatus: string;
  isDealerPurchased: boolean;
  orderId: string;
  isCashAdvance: boolean;
  isVscEligible: boolean;
  soldOnLot: boolean;
  coBuyer: CoBuyer;
  primary: Primary;
}

export const rollBack = (request: RollbackRequest) =>
  axios.post(APP_ENDPOINTS.ROLLBACK, request).then(() => void 0);

export const getAvailableOptionsForOrder = (abortSignal?: AbortSignal): Promise<AvailableOptions> =>
  axios
    .get<AvailableOptions>(APP_ENDPOINTS.GET_AVAILABLE_OPTIONS, {
      signal: abortSignal
    })
    .then(res => res.data);

export const pauseLockTimer = () =>
  axios.post(APP_ENDPOINTS.PAUSE_LOCK_TIMER, null).then(() => void 0);
